<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import type { BranchAddressShowItem } from '../utils/contactBranchAddressShow';

const props = withDefaults(
    defineProps<{
        modelValue: boolean;
        items: BranchAddressShowItem[];
        addressTypeLabel?: string;
    }>(),
    { items: () => [], addressTypeLabel: '' }
);

const emit = defineEmits<{
    (e: 'update:modelValue', val: boolean): void;
    (e: 'select', row: Record<string, unknown>): void;
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

const dialog = ref(props.modelValue);
watch(() => props.modelValue, (v) => { dialog.value = v; });
watch(dialog, (v) => emit('update:modelValue', v));

const search = ref('');
const selectedId = ref<string | null>(null);

const filteredItems = computed(() => {
    const q = String(search.value || '').trim().toLowerCase();
    if (!q) return props.items;
    return props.items.filter((item) => {
        const hay = [item.displayLine, item.alias, item.addressTypeLabel, props.addressTypeLabel]
            .map((v) => String(v ?? '').toLowerCase())
            .join(' ');
        return hay.includes(q);
    });
});

watch(dialog, (open) => {
    if (open) {
        search.value = '';
        selectedId.value = null;
    }
});

function toggleSelection(id: string) {
    selectedId.value = selectedId.value === id ? null : id;
}

function applySelection() {
    const id = selectedId.value;
    if (!id) return;
    const item = filteredItems.value.find((x) => x.id === id);
    if (!item) return;
    emit('select', item.row);
    dialog.value = false;
}
</script>

<template>
  <v-dialog v-model="dialog" max-width="1080" width="92vw" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center flex-wrap ga-2 py-3">
        <v-icon color="primary">mdi-map-marker-multiple</v-icon>
        <span class="text-truncate flex-grow-1">
          {{ t('contact.Show Address') || 'Show Address' }}
          <span v-if="addressTypeLabel" class="text-medium-emphasis text-body-2 ms-1">
            ({{ addressTypeLabel }})
          </span>
        </span>
        <v-chip v-if="items.length > 0" size="small" variant="tonal" color="primary">
          {{ items.length }} {{ t('contact.total') || 'total' }}
        </v-chip>
        <v-btn icon variant="text" size="small" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <div class="px-4 py-3">
        <v-text-field
          v-model="search"
          :placeholder="t('contact.Search addresses') || 'Search by address or type...'"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
        />
      </div>

      <v-card-text class="pa-0" style="max-height: 60vh; overflow-y: auto;">
        <div v-if="filteredItems.length === 0" class="pa-8 text-center text-medium-emphasis">
          <v-icon size="48" color="grey-lighten-1">mdi-map-marker-off-outline</v-icon>
          <p class="mt-3 mb-0">{{ t('contact.No address candidates') || 'No address candidates' }}</p>
        </div>
        <v-table v-else density="compact" class="contact-branch-address-show-table">
          <thead>
            <tr>
              <th style="width: 48px" />
              <th style="width: 140px">{{ t('contact.Address Type') || 'Address Type' }}</th>
              <th style="width: 120px">{{ t('contact.Alias') || 'Alias' }}</th>
              <th>{{ t('contact.Address') || 'Address' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredItems"
              :key="item.id"
              class="cursor-pointer"
              @click="toggleSelection(item.id)"
            >
              <td @click.stop>
                <v-checkbox-btn
                  :model-value="selectedId === item.id"
                  density="compact"
                  @update:model-value="toggleSelection(item.id)"
                />
              </td>
              <td class="text-body-2">
                <span v-if="item.addressTypeLabel" class="text-caption font-weight-medium">
                  {{ item.addressTypeLabel }}
                </span>
                <span v-else class="text-medium-emphasis">-</span>
              </td>
              <td class="text-body-2">
                <v-chip
                  v-if="item.alias"
                  size="x-small"
                  variant="tonal"
                  color="primary"
                  class="contact-branch-show-alias-chip font-weight-medium"
                  label
                >
                  {{ item.alias }}
                </v-chip>
                <span v-else class="text-medium-emphasis">-</span>
              </td>
              <td class="text-caption">{{ item.displayLine }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn variant="text" @click="dialog = false">{{ t('contact.Cancel') || 'Cancel' }}</v-btn>
        <v-btn color="primary" variant="flat" :disabled="!selectedId" @click="applySelection">
          {{ t('contact.Apply') || 'Apply' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.contact-branch-address-show-table tbody tr:hover {
  background: rgba(var(--v-theme-primary), 0.04);
}
.cursor-pointer {
  cursor: pointer;
}
.contact-branch-show-alias-chip {
  height: 22px !important;
  font-size: 0.7rem !important;
}
</style>
